import * as React from 'react'

import Link from 'next/link'
import styled, { css } from 'styled-components'

import * as contentful from '@boutique/contentful'
import * as ds from '@bob/design-system'
import { styles as S } from '@bob/design-system'
import * as url from '@bob/url'
import * as utils from '@bob/utils'

type PillarAccordionProps = {
  pillar: string
  brush: string
  criteria: {
    description: string
    icon: contentful.PillarIcon
    name: string
    slug: string
  }[]
}

export function PillarAccordion({
  pillar,
  brush,
  criteria,
}: PillarAccordionProps): React.ReactElement<PillarAccordionProps> {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <Accordion
      title={
        <Brush as="h4" $color={brush}>
          {pillar}
        </Brush>
      }
      isOpen={isOpen}
      onToggle={() => {
        if (ds.utils.browser.isMobile()) {
          setIsOpen(!isOpen)
        }
      }}
    >
      <CriteriaList>
        {criteria.map((criterion) => {
          const blobData = utils.blob.useBlobData()
          return (
            <CriteriaItem key={criterion.name}>
              <Link
                href={
                  url.tag({
                    name: criterion.name,
                    slug: criterion.slug,
                  }).path
                }
                prefetch={false}
                legacyBehavior
              >
                <a>
                  <CriteriaHead>
                    <ds.BlobBackground
                      color={blobData.color}
                      marginBottom={50 * (1 / 9)}
                      marginLeft={50 * (1 / 9)}
                      marginRight={50 * (1 / 9)}
                      marginTop={50 * (1 / 9)}
                      variant={blobData.variant}
                    />
                    <CriteriaIcon icon={criterion.icon} size={25} />
                    <S.copy.Text3 as="h4">{criterion.name}</S.copy.Text3>
                  </CriteriaHead>
                </a>
              </Link>
              <S.copy.Text4 as="p">{criterion.description}</S.copy.Text4>
            </CriteriaItem>
          )
        })}
      </CriteriaList>
    </Accordion>
  )
}

type CriteriaIconProps = {
  icon: contentful.PillarIcon
} & ds.illustrations.SpecificIllustrationProps

function CriteriaIcon({ icon, ...rest }: CriteriaIconProps) {
  switch (icon) {
    case 'carbon-neutral': {
      return <ds.illustrations.criteria.CarbonNeutral {...rest} />
    }
    case 'circular-economy': {
      return <ds.illustrations.criteria.CircularEconomy {...rest} />
    }
    case 'fair-trade': {
      return <ds.illustrations.criteria.FairTrade {...rest} />
    }
    case 'inventory-optimized': {
      return <ds.illustrations.criteria.OptimizedInventory {...rest} />
    }
    case 'made-in-europe': {
      return <ds.illustrations.criteria.MadeInEurope {...rest} />
    }
    case 'made-in-france': {
      return <ds.illustrations.criteria.MadeInFrance {...rest} />
    }
    case 'natural-materials': {
      return <ds.illustrations.criteria.NaturalMaterials {...rest} />
    }
    case 'organic': {
      return <ds.illustrations.criteria.Organic {...rest} />
    }
    case 'recycled-materials': {
      return <ds.illustrations.criteria.RecycledMaterials {...rest} />
    }
    case 'short-supply-chain': {
      return <ds.illustrations.criteria.ShortSupplyChain {...rest} />
    }
    case 'sustainable': {
      return <ds.illustrations.criteria.Sustainable {...rest} />
    }
    case 'upcycled-materials': {
      return <ds.illustrations.criteria.UpcycledMaterials {...rest} />
    }
    case 'zero-waste': {
      return <ds.illustrations.criteria.ZeroWaste {...rest} />
    }
  }
}

const CriteriaHead = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 4px;

  ${ds.BlobBackground} {
    height: 34px;
    opacity: 0;
    padding: 18px;
    position: absolute;
    transform: scale(0.7);
    transition: opacity 200ms ease-in-out, transform 200ms ease-in-out;
    width: 34px;
    z-index: 0;
  }

  ${ds.illustrations.Illustration} {
    margin-right: 3px;
    visibility: visible;
    z-index: 0;
  }

  ${S.copy.Text3} {
    margin-left: 10px;
  }

  &:hover {
    ${ds.BlobBackground} {
      opacity: 1;
      transform: scale(1);
    }
  }
`

const CriteriaList = styled.ul``

const CriteriaItem = styled.li`
  margin: 25px 0;

  ${S.copy.Text4} {
    margin-left: 38px;
  }
`

function brushBackground($color: string) {
  return ds.utils.svg.toDataUrl(ds.brush.full.FullBrush3, {
    width: 100,
    fill: $color,
  })
}

const Brush = styled(S.heading.TitleCardArticle)<{ $color: string }>`
  ${({ $color }) => {
    return css`
      background-image: url('${brushBackground($color)}');
    `
  }}
  background-size: 100px auto;
  background-repeat: no-repeat;
  background-position: left center;
  padding: 1em;
  margin: -1em;
`

const Accordion = styled(ds.Accordion)`
  margin-top: 27px;

  @media screen and (${ds.breakpoint.TABLET}) {
    ${ds.icons.ChevronUp}, ${ds.icons.ChevronDown} {
      display: none;
    }

    ${S.accordion.Content} {
      display: block;
    }

    ${S.accordion.Toggle} {
      cursor: default;
    }
  }

  ${S.accordion.Toggle} {
    margin-bottom: 7px;
  }
`
